import { Injectable, OnDestroy } from '@angular/core';
import { IReportData } from '../../../models/reports/IReportData';
import { TranslateService } from '@ngx-translate/core';
import { ITableData } from '../../../models/reports/ITableData';
import { BaseTasks, Condition, LaborData, LaborLevelwiseData, OtPwrSalesOfficeCollection, OverTimePWRAdjustment,SalesOfficeDetails, SQWebArea, SQWebProject, SQWebSystem, Tasks, TypicalOfSystemHelper } from '../../../models/dtos';
import { CommonDataService } from '../../common-data.service';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { IDataItem } from '../../../models/reports/IDataItem';
import { LaborHoursDistColumnEnum, LaborHoursDistRowEnum } from '../../../models/enum';
import { RowSumArray } from '../../../models/reports/RowSumArray';
import { LaborHoursRow } from '../../../models/reports/LaborHoursRow';
import { LaborHoursByLevelSum } from '../../../models/reports/LaborHoursByLevelSum';
import { LaborHoursFooterSum } from '../../../models/reports/LaborHoursFooterSum';
import { AggregatedTask } from '../../../models/reports/IAggregatedTask';
import { IndexDBService } from '../../common/indexDB.service';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { IOfficeLaborTotals } from '../../../models/reports/IOfficeLaborTotals';
import { CommonMetaDataService } from '../../common-meta-data.service';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';

function createEmptyLaborHoursFooterSum(): LaborHoursFooterSum {
  return { pm: 0, eng: 0, ceng: 0, eeng: 0, leng: 0, neng: 0, qeng: 0, grap: 0, prog: 0, spec: 0, dds: 0, mech: 0, elec: 0 };
}

@Injectable()
export class SolLaborReportsService implements OnDestroy {

  private laborLevelwiseData: LaborLevelwiseData;
  private sqWebProject: SQWebProject;
  private laborHoursDistRowInfoList: ITableData[] = [];
  private laborHoursByLevelInfoList: ITableData[] = [];
  private taskByLevelWithNotesInfoList: ITableData[] = [];
  private taskByLevelInfoList: ITableData[] = [];
  private taskByLevelWithNotesSummaryMap: Map<string, AggregatedTask> = new Map();
  private taskByLevelSummaryMap: Map<string, AggregatedTask> = new Map();
  private readonly VALUE_ZERO = 0.00;
  reportGenerated: boolean = false;
  private labourHoursByOfficeInfoList: ITableData[] = [];
  private salesOfficesDetails: SalesOfficeDetails[] = [];
  private readonly salesOfficeNameById: Map<number, string> = new Map();
  private processedTypicalSystemIdsPerArea: Map<string, Set<string>> = new Map();
  private processedTypicalSystemIdsForTaskReports: Set<string> = new Set();
  // Typical-of groups whose DXR base hours are already in Labor Hours by Level (counted once across areas).
  private dxrCountedTypicalIds: Set<string> = new Set();
  private typicalOfHelpers: Record<string, TypicalOfSystemHelper> = {};
  private estimateTotalBaseHours: LaborHoursFooterSum = createEmptyLaborHoursFooterSum();
  private estimateTotalTaskAdjustments: LaborHoursFooterSum = createEmptyLaborHoursFooterSum();
  private estimateTotalConditionalAdjustments: LaborHoursFooterSum = createEmptyLaborHoursFooterSum();

  constructor(
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly indexDbService: IndexDBService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe
  ) { }

  private formatHours(value: number): string {
    return this.decimalPrecisionPipe.transform(value);
  }
  
  private createEmptyRowSumArray(): RowSumArray {
    return [
      ["STRAIGHT_TIME", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["SHIFT_DIFFERENTIAL_RATE", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["OVERTIME_TIME_AND_1/2", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["OVERTIME_DOUBLE_TIME", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["PREVAILING_WAGE_RATE", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["PWR_@_SHIFTS_DIFFERENTIAL_RATE", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["PREVAILING_WAGE_RATE_TIME_AND_1/2", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
      ["PREVAILING_WAGE_RATE_DOUBLE_TIME", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO],
    ];
  }

  private calculateFooterTotals(rowSumArray: RowSumArray): LaborHoursRow {
    const footerSumArr: LaborHoursRow = ["", "", CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO, CONSTANTS.ZERO];
    
    rowSumArray.forEach((row) => {
      footerSumArr[LaborHoursDistColumnEnum.PM] += row[LaborHoursDistColumnEnum.PM];
      footerSumArr[LaborHoursDistColumnEnum.ENG] += row[LaborHoursDistColumnEnum.ENG];
      footerSumArr[LaborHoursDistColumnEnum.CENG] += row[LaborHoursDistColumnEnum.CENG];
      footerSumArr[LaborHoursDistColumnEnum.EENG] += row[LaborHoursDistColumnEnum.EENG];
      footerSumArr[LaborHoursDistColumnEnum.LENG] += row[LaborHoursDistColumnEnum.LENG];
      footerSumArr[LaborHoursDistColumnEnum.NENG] += row[LaborHoursDistColumnEnum.NENG];
      footerSumArr[LaborHoursDistColumnEnum.QENG] += row[LaborHoursDistColumnEnum.QENG];
      footerSumArr[LaborHoursDistColumnEnum.GRAP] += row[LaborHoursDistColumnEnum.GRAP];
      footerSumArr[LaborHoursDistColumnEnum.PROG] += row[LaborHoursDistColumnEnum.PROG];
      footerSumArr[LaborHoursDistColumnEnum.SPEC] += row[LaborHoursDistColumnEnum.SPEC];
      footerSumArr[LaborHoursDistColumnEnum.DDS] += row[LaborHoursDistColumnEnum.DDS];
      footerSumArr[LaborHoursDistColumnEnum.MECH] += row[LaborHoursDistColumnEnum.MECH];
      footerSumArr[LaborHoursDistColumnEnum.ELEC] += row[LaborHoursDistColumnEnum.ELEC];
    });
    
    return footerSumArr;
  }

  private addRowsToDistributionTable(tableIndex: number, rowSumArray: RowSumArray): void {
    rowSumArray.forEach((row) => {
      this.laborHoursDistRowInfoList[tableIndex].data.push({
        data: this.fillDataInTableRow([
          this.translate.instant(row[LaborHoursDistColumnEnum.DESCRIPTION]),
          row[LaborHoursDistColumnEnum.NUMBER_IN_AREA],
          this.formatHours(row[LaborHoursDistColumnEnum.PM]),
          this.formatHours(row[LaborHoursDistColumnEnum.ENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.CENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.EENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.LENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.NENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.QENG]),
          this.formatHours(row[LaborHoursDistColumnEnum.GRAP]),
          this.formatHours(row[LaborHoursDistColumnEnum.PROG]),
          this.formatHours(row[LaborHoursDistColumnEnum.SPEC]),
          this.formatHours(row[LaborHoursDistColumnEnum.DDS]),
          this.formatHours(row[LaborHoursDistColumnEnum.MECH]),
          this.formatHours(row[LaborHoursDistColumnEnum.ELEC])
        ])
      });
    });
  }

  private addFooterToDistributionTable(tableIndex: number, footerSumArr: LaborHoursRow): void {
    this.laborHoursDistRowInfoList[tableIndex].tableFooter = [{
      headers: [
        { headerText: this.translate.instant('TOTAL_HOURS'), columnspan: CONSTANTS.ONE, alignment: "left" },
        { headerText: "", columnspan: CONSTANTS.ONE },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.PM]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.ENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.CENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.EENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.LENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.NENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.QENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.GRAP]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.PROG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.SPEC]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.DDS]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.MECH]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(footerSumArr[LaborHoursDistColumnEnum.ELEC]), columnspan: CONSTANTS.ONE, alignment: "center" }
      ]
    }];
  }

  private accumulateHoursToSum(
    sumArray: LaborHoursByLevelSum,
    hours: LaborHoursFooterSum,
    multiplier: number = CONSTANTS.ONE
  ): void {
    sumArray[LaborHoursDistColumnEnum.PM] += hours.pm * multiplier;
    sumArray[LaborHoursDistColumnEnum.ENG] += hours.eng * multiplier;
    sumArray[LaborHoursDistColumnEnum.CENG] += hours.ceng * multiplier;
    sumArray[LaborHoursDistColumnEnum.EENG] += hours.eeng * multiplier;
    sumArray[LaborHoursDistColumnEnum.LENG] += hours.leng * multiplier;
    sumArray[LaborHoursDistColumnEnum.NENG] += hours.neng * multiplier;
    sumArray[LaborHoursDistColumnEnum.QENG] += hours.qeng * multiplier;
    sumArray[LaborHoursDistColumnEnum.GRAP] += hours.grap * multiplier;
    sumArray[LaborHoursDistColumnEnum.PROG] += hours.prog * multiplier;
    sumArray[LaborHoursDistColumnEnum.SPEC] += hours.spec * multiplier;
    sumArray[LaborHoursDistColumnEnum.DDS] += hours.dds * multiplier;
    sumArray[LaborHoursDistColumnEnum.MECH] += hours.mech * multiplier;
    sumArray[LaborHoursDistColumnEnum.ELEC] += hours.elec * multiplier;
  }

  private addToFooterSum(
    target: LaborHoursFooterSum,
    source: LaborHoursFooterSum,
    multiplier: number = CONSTANTS.ONE
  ): void {
    target.pm += source.pm * multiplier;
    target.eng += source.eng * multiplier;
    target.ceng += source.ceng * multiplier;
    target.eeng += source.eeng * multiplier;
    target.leng += source.leng * multiplier;
    target.neng += source.neng * multiplier;
    target.qeng += source.qeng * multiplier;
    target.grap += source.grap * multiplier;
    target.prog += source.prog * multiplier;
    target.spec += source.spec * multiplier;
    target.dds += source.dds * multiplier;
    target.mech += source.mech * multiplier;
    target.elec += source.elec * multiplier;
  }


  generateLaborHoursDistributionTitleHeaderRow(LevelName: string, hasTypicalOf: boolean, typicalOfCount: number, typicalOfCountInArea: number): IDataItem[] {
    const typicalOfLogo = hasTypicalOf ? CONSTANTS.TYPICAL_OF_LOGO : "";
    const typicalOfTotalCount = hasTypicalOf && typicalOfCount > CONSTANTS.ZERO ? `of ${typicalOfCount}` : "";
    const typicalOfTotalCountInArea  = hasTypicalOf && typicalOfCountInArea > CONSTANTS.ZERO ? `[ ${typicalOfCountInArea} ${this.translate.instant("IN_AREA")} ]` : "";
    // Logo + typical-of count + level name share one physical column (width = sum of the 3 former
    // columns) instead of 3 separate columns, so no columnspan/table-generator changes are needed.
    const combinedLevelLabel = [typicalOfLogo, typicalOfTotalCount, this.translate.instant(LevelName)].filter(Boolean).join(' ');
    return [
      { data: combinedLevelLabel, columnspan: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, hierarchyLevel: CONSTANTS.ZERO, width: "170", alignment: 'left' },
      { data: typicalOfTotalCountInArea, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO},
      { data: this.translate.instant('PM'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('ENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('CENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('EENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('LENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('NENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('QENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('GRAP'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('PROG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('SPEC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('DDS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('MECH'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: this.translate.instant('ELEC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
    ]
  }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    this.resetValues();
    let reportData: IReportData;
    this.salesOfficesDetails = this.commonMetaDataService.getMetaData()?.salesOfficesDetails || [];
    this.typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
    this.sqWebProject = this.commonDataService.getSQWebProject();
    if (!this.sqWebProject) {
      return;
    }
    this.laborLevelwiseData = await this.indexDbService.getAllLaborData();
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    reportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: this.generateTableData(reportId),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  generateTableData(reportId: ReportList): ITableData[] {
    if(!this.reportGenerated){
      this.generateLaborReports();
      this.reportGenerated = true;
    }

    switch (reportId) {
      case ReportList.LaborHoursDistViewModel:
        return this.laborHoursDistRowInfoList;
      case ReportList.MaterialTakeOffAndExtensionReportViewModel:
        return this.laborHoursByLevelInfoList;
      case ReportList.TaskByLevelWithNotesViewModel:
        return this.taskByLevelWithNotesInfoList;
      case ReportList.TaskByLevelReportViewModel:
        return this.taskByLevelInfoList;
      case ReportList.LaborHoursBySalesOfficeReportViewModel:
        return this.labourHoursByOfficeInfoList;
      default:
        return [];
    }
    
  }

  private generateLaborReports() {

    this.processAllLevels();
    this.removeLastPageBreakAfterIfExists(this.laborHoursDistRowInfoList);

  }

  private removeLastPageBreakAfterIfExists(tableList: ITableData[]): void{
    if (tableList.length) {
      const lastTable = tableList[tableList.length - CONSTANTS.ONE];
      lastTable.isPageBreakAfter = false;
    }
  }

  private fillDataInTableRow(dataList: string[]): IDataItem[] {
      let dataRow: IDataItem[] =
      dataList.map((element, index) => {
        return { 
                    data: element, 
                    isSubHeader: false, 
                    columnspan: CONSTANTS.ONE, 
                    hierarchyLevel: CONSTANTS.ZERO, 
                    alignment: index ? CONSTANTS.CENTER_ALIGNMENT : CONSTANTS.LEFT_ALIGNMENT 
                  }
      });
      return dataRow;
  }

  generateLaborHoursDistributionTableByName(
    levelName: string, 
    hasTypicalOf: boolean = false, 
    typicalOfCount: number = CONSTANTS.ZERO, 
    typicalOfCountInArea: number = CONSTANTS.ZERO): void {
    
    this.laborHoursDistRowInfoList.push({
      title: this.generateLaborHoursDistributionTitleHeaderRow(levelName, hasTypicalOf, typicalOfCount, typicalOfCountInArea),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: this.laborHoursDistRowInfoList.length % 2 === CONSTANTS.ONE // Add page break after every 2 tables
    });
  }

  /**
   * DXR rules shared by the Task by Level, Labor Hours by Level and Labor Hours by Sales Office reports:
   * 1. DXR base tasks (`baseTasksDXRCollection`) are only taken for a DXR *master* system.
   * 2. For a typical-of group the DXR data lives once on the group's holder labor data, and the
   *    master may not be the member listed first, so the master is looked up across the whole group.
   * 3. DXR base tasks are one-time work: they are NOT multiplied by the typical-of count
   *    (base and adjustment tasks are), and a group's DXR hours are counted once, not per member.
   */
  private isDxrMasterSystem(system: SQWebSystem): boolean {
    if (!system.isSystemDXR) {
      return false;
    }
    const typicalId = system.systemTypicalOfHelperUniqueId;
    const group = typicalId ? this.typicalOfHelpers[typicalId]?.typicalOfSystems : undefined;
    return group?.length ? group.some(member => member.isMaster) : !!system.isMaster;
  }

  private replaceNegativeInfiniteNine(value: number): number {
    let removeNegativeInfiniteNineList = [ -999999999, -9999999, -99999 ];
    return removeNegativeInfiniteNineList.includes(value) ? this.VALUE_ZERO : value;
  }

  private hasNonZeroLaborHours(hours: LaborHoursFooterSum): boolean {
    return hours.pm !== 0 || hours.eng !== 0 || hours.ceng !== 0 || hours.eeng !== 0 ||
           hours.leng !== 0 || hours.neng !== 0 || hours.qeng !== 0 || hours.grap !== 0 ||
           hours.prog !== 0 || hours.spec !== 0 || hours.dds !== 0 || hours.mech !== 0 || hours.elec !== 0;
  }

  private groupAndAggregateTasks(baseTasks: BaseTasks, adjustmentTasks: Tasks): Map<string, AggregatedTask> {
    const taskMap = new Map<string, AggregatedTask>();
    
    // Process both base tasks and adjustment tasks
    const allTasks = [...baseTasks, ...adjustmentTasks];
    
    for (const task of allTasks) {
      
      const normalizedNotes = task.laborTabNotes || "";
      const key = `${task.referenceNumber}|${task.description}|${normalizedNotes}|${task.salesOfficeId}`;
      
      if (taskMap.has(key)) {
        // Aggregate hours if task already exists
        const existing = taskMap.get(key);
        existing.pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor);
        existing.eng += this.replaceNegativeInfiniteNine(task.engineerLabor);
        existing.ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
        existing.eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
        existing.leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
        existing.neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
        existing.qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);
        existing.grap += this.replaceNegativeInfiniteNine(task.graphicsLabor);
        existing.prog += this.replaceNegativeInfiniteNine(task.programmingLabor);
        existing.spec += this.replaceNegativeInfiniteNine(task.specialistLabor);
        existing.dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
        existing.mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
        existing.elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
      } else {
        // Create new entry
        taskMap.set(key, {
          referenceNumber: task.referenceNumber,
          description: task.description,
          notes: normalizedNotes,
          salesOfficeId: task.salesOfficeId,
          pm: this.replaceNegativeInfiniteNine(task.projectManagementLabor),
          eng: this.replaceNegativeInfiniteNine(task.engineerLabor),
          ceng: this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor),
          eeng: this.replaceNegativeInfiniteNine(task.energyEngineerLabor),
          leng: this.replaceNegativeInfiniteNine(task.lengEngineerLabor),
          neng: this.replaceNegativeInfiniteNine(task.networkEngineerLabor),
          qeng: this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor),
          grap: this.replaceNegativeInfiniteNine(task.graphicsLabor),
          prog: this.replaceNegativeInfiniteNine(task.programmingLabor),
          spec: this.replaceNegativeInfiniteNine(task.specialistLabor),
          dds: this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor),
          mech: this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor),
          elec: this.replaceNegativeInfiniteNine(task.electricalEngineerLabor)
        });
      }
    }
    
    return taskMap;
  }

  private accumulateTaskIntoWithNotesSummary(task: AggregatedTask): void {
    const summaryKey = `${task.referenceNumber}|${task.description}|${task.salesOfficeId}`;
    
    if (this.taskByLevelWithNotesSummaryMap.has(summaryKey)) {
      // Aggregate hours if task already exists in summary
      const existing = this.taskByLevelWithNotesSummaryMap.get(summaryKey)!;
      existing.pm += task.pm;
      existing.eng += task.eng;
      existing.ceng += task.ceng;
      existing.eeng += task.eeng;
      existing.leng += task.leng;
      existing.neng += task.neng;
      existing.qeng += task.qeng;
      existing.grap += task.grap;
      existing.prog += task.prog;
      existing.spec += task.spec;
      existing.dds += task.dds;
      existing.mech += task.mech;
      existing.elec += task.elec;
    } else {
      // Create new entry in summary (notes always empty)
      this.taskByLevelWithNotesSummaryMap.set(summaryKey, {
        referenceNumber: task.referenceNumber,
        description: task.description,
        notes: "",
        salesOfficeId: task.salesOfficeId,
        pm: task.pm,
        eng: task.eng,
        ceng: task.ceng,
        eeng: task.eeng,
        leng: task.leng,
        neng: task.neng,
        qeng: task.qeng,
        grap: task.grap,
        prog: task.prog,
        spec: task.spec,
        dds: task.dds,
        mech: task.mech,
        elec: task.elec
      });
    }
  }


  private accumulateTaskIntoSummary(task: AggregatedTask): void {
    const summaryKey = `${task.referenceNumber}|${task.description}|${task.salesOfficeId}`;
    
    if (this.taskByLevelSummaryMap.has(summaryKey)) {
      // Aggregate hours if task already exists in summary
      const existing = this.taskByLevelSummaryMap.get(summaryKey)!;
      existing.pm += task.pm;
      existing.eng += task.eng;
      existing.ceng += task.ceng;
      existing.eeng += task.eeng;
      existing.leng += task.leng;
      existing.neng += task.neng;
      existing.qeng += task.qeng;
      existing.grap += task.grap;
      existing.prog += task.prog;
      existing.spec += task.spec;
      existing.dds += task.dds;
      existing.mech += task.mech;
      existing.elec += task.elec;
    } else {
      // Create new entry in summary (notes always empty)
      this.taskByLevelSummaryMap.set(summaryKey, {
        referenceNumber: task.referenceNumber,
        description: task.description,
        notes: "",
        salesOfficeId: task.salesOfficeId,
        pm: task.pm,
        eng: task.eng,
        ceng: task.ceng,
        eeng: task.eeng,
        leng: task.leng,
        neng: task.neng,
        qeng: task.qeng,
        grap: task.grap,
        prog: task.prog,
        spec: task.spec,
        dds: task.dds,
        mech: task.mech,
        elec: task.elec
      });
    }
  }

  // Helper method to accumulate footer totals from a task
  private accumulateFooterTotals(
    footerSum: LaborHoursFooterSum,
    task: AggregatedTask
  ): void {
    footerSum.pm += task.pm;
    footerSum.eng += task.eng;
    footerSum.ceng += task.ceng;
    footerSum.eeng += task.eeng;
    footerSum.leng += task.leng;
    footerSum.neng += task.neng;
    footerSum.qeng += task.qeng;
    footerSum.grap += task.grap;
    footerSum.prog += task.prog;
    footerSum.spec += task.spec;
    footerSum.dds += task.dds;
    footerSum.mech += task.mech;
    footerSum.elec += task.elec;
  }

  // Generate summary table from accumulated data for Task By Level With Notes report
  private generateTaskByLevelWithNotesSummaryTable(): void {
    // Filter out zero-hour tasks from summary
     const summaryTasks = Array.from(this.taskByLevelWithNotesSummaryMap.values()).filter(task => this.hasNonZeroLaborHours(task));
    
    // Only create summary table if there are valid tasks
    if (summaryTasks.length === 0) {
      return;
    }
    
    // Create summary table
    this.generateTaskByLevelWithNotesTable('SUMMARY');
    const tableIndex = this.taskByLevelWithNotesInfoList.length - CONSTANTS.ONE;
    
    // Footer accumulator
    const summaryFooterSum = {
      pm: CONSTANTS.ZERO, eng: CONSTANTS.ZERO, ceng: CONSTANTS.ZERO, eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO, neng: CONSTANTS.ZERO, qeng: CONSTANTS.ZERO, grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO, spec: CONSTANTS.ZERO, dds: CONSTANTS.ZERO, mech: CONSTANTS.ZERO, elec: CONSTANTS.ZERO
    };
    
    // Add rows from summary
    for (const task of summaryTasks) {
      this.taskByLevelWithNotesInfoList[tableIndex].data.push({
        data: [
          { data: task.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: task.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: this.formatHours(task.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.getSalesOfficeDescriptionById(task.salesOfficeId), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: task.notes || "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });
      
      // Accumulate footer totals
      this.accumulateFooterTotals(summaryFooterSum, task);
    }
    
    // Add footer only if there are non-zero totals
    if (this.hasNonZeroLaborHours(summaryFooterSum)) {
      this.taskByLevelWithNotesInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.translate.instant("HOURS_TOTAL"), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.formatHours(summaryFooterSum.pm), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.eng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.ceng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.eeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.leng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.neng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.qeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.grap), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.prog), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.spec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.dds), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.mech), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.elec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" }
        ]
      });
    }
    
    // Move summary table to the beginning
    if (this.taskByLevelWithNotesInfoList.length > CONSTANTS.ONE) {
      const summaryTable = this.taskByLevelWithNotesInfoList.pop();
      if (summaryTable) {
        this.taskByLevelWithNotesInfoList.unshift(summaryTable);
      }
    }
  }

  // Apply multiplier to all labor hour fields on each task
  private applyMultiplierToTasks(tasks: AggregatedTask[], multiplier: number): void {
    if (multiplier > CONSTANTS.ONE) {
      for (const task of tasks) {
        task.pm *= multiplier;
        task.eng *= multiplier;
        task.ceng *= multiplier;
        task.eeng *= multiplier;
        task.leng *= multiplier;
        task.neng *= multiplier;
        task.qeng *= multiplier;
        task.grap *= multiplier;
        task.prog *= multiplier;
        task.spec *= multiplier;
        task.dds *= multiplier;
        task.mech *= multiplier;
        task.elec *= multiplier;
      }
    }
  }

  /**
   * Aggregates one level's tasks for the Task by Level reports and returns the non-zero rows.
   * Base and adjustment tasks are scaled by the typical-of multiplier first; DXR base tasks are
   * merged in afterwards, unscaled, because they are one-time (see isDxrMasterSystem).
   * `group` builds the row key, which differs between the with-notes and without-notes reports.
   */
  private aggregateLevelTasks(
    levelLaborData: LaborData,
    multiplier: number,
    includeDxrBaseTasks: boolean,
    group: (baseTasks: any[], adjustmentTasks: any[]) => Map<string, AggregatedTask>
  ): AggregatedTask[] {
    const taskMap = group(levelLaborData.baseTasksCollection, levelLaborData.tasksCollection);
    this.applyMultiplierToTasks(Array.from(taskMap.values()), multiplier);

    if (includeDxrBaseTasks) {
      for (const [key, dxrTask] of group(levelLaborData.baseTasksDXRCollection ?? [], [])) {
        const existing = taskMap.get(key);
        if (existing) {
          this.accumulateFooterTotals(existing, dxrTask);
        } else {
          taskMap.set(key, dxrTask);
        }
      }
    }

    return Array.from(taskMap.values()).filter(task => this.hasNonZeroLaborHours(task));
  }

  // Reusable method to process tasks for Task by Level with Notes report
  private processTasksByLevel(
    levelLaborData: LaborData, 
    levelName: string, 
    multiplier: number = CONSTANTS.ONE,
    includeDxrBaseTasks: boolean = false): void {

    if(!levelLaborData){
      return;
    }

    const validTasks = this.aggregateLevelTasks(levelLaborData, multiplier, includeDxrBaseTasks,
      (base, adjustment) => this.groupAndAggregateTasks(base, adjustment));
    
    // Only create table if there are valid tasks
    if (validTasks.length === 0) {
      return;
    }
    
    // Create table
    this.generateTaskByLevelWithNotesTable(levelName);
    const tableIndex = this.taskByLevelWithNotesInfoList.length - CONSTANTS.ONE;
    
    // Footer accumulator
    const footerSum = {
      pm: CONSTANTS.ZERO, eng: CONSTANTS.ZERO, ceng: CONSTANTS.ZERO, eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO, neng: CONSTANTS.ZERO, qeng: CONSTANTS.ZERO, grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO, spec: CONSTANTS.ZERO, dds: CONSTANTS.ZERO, mech: CONSTANTS.ZERO, elec: CONSTANTS.ZERO
    };
    
    // Add rows and accumulate into summary
    for (const task of validTasks) {
      this.taskByLevelWithNotesInfoList[tableIndex].data.push({
        data: [
          { data: task.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: task.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: this.formatHours(task.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.getSalesOfficeDescriptionById(task.salesOfficeId), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: task.notes || "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });
      
      // Accumulate footer totals
      footerSum.pm += task.pm;
      footerSum.eng += task.eng;
      footerSum.ceng += task.ceng;
      footerSum.eeng += task.eeng;
      footerSum.leng += task.leng;
      footerSum.neng += task.neng;
      footerSum.qeng += task.qeng;
      footerSum.grap += task.grap;
      footerSum.prog += task.prog;
      footerSum.spec += task.spec;
      footerSum.dds += task.dds;
      footerSum.mech += task.mech;
      footerSum.elec += task.elec;
      
      // Accumulate into summary map (aggregate across all levels)
      this.accumulateTaskIntoWithNotesSummary(task);
    }
    
    // Add footer only if there are non-zero totals
    if (this.hasNonZeroLaborHours(footerSum)) {
      this.taskByLevelWithNotesInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.translate.instant("HOURS_TOTAL"), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.formatHours(footerSum.pm), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.eng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.ceng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.eeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.leng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.neng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.qeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.grap), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.prog), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.spec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.dds), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.mech), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.elec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" }
        ]
      });
    }
  }

  // Process project-level tasks for Task by Level with Notes report
  private getProjectTaskByLevel(): void {
    const projectLaborData = this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId];
    this.processTasksByLevel(projectLaborData, 'ESTIMATE_LEVEL');
  }

  // Process area-level tasks for Task By Level with Notes report
  private getAreaTaskByLevel(area: SQWebArea): void {
    const areaLaborData = this.laborLevelwiseData.laborsCollection[area.itemUniqueId];
    this.processTasksByLevel(areaLaborData, area.itemName);
  }

  // Process system-level tasks for Task by Level with Notes report
  private getSystemTaskByLevel(
    system: SQWebSystem, 
    laborDataId: string, 
    typicalOfCount: number,
    visibleTypicalOfCount: number = typicalOfCount): void {

    const dataId = laborDataId || system.itemUniqueId;
    const systemLaborData = this.laborLevelwiseData.laborsCollection[dataId];
    const levelName = typicalOfCount > 0
      ? `${CONSTANTS.TYPICAL_OF_LOGO} of ${typicalOfCount} ${system.itemName}`
      : system.itemName;
    const multiplier = visibleTypicalOfCount ? visibleTypicalOfCount : CONSTANTS.ONE;
    this.processTasksByLevel(systemLaborData, levelName, multiplier, this.isDxrMasterSystem(system));
  }

  // ========== Task By Level Report Methods (without notes) ==========

  private groupAndAggregateTasksWithoutNotes(baseTasks: any[], adjustmentTasks: any[]): Map<string, AggregatedTask> {
    const taskMap = new Map<string, AggregatedTask>();
    
    // Process both base tasks and adjustment tasks
    const allTasks = [...baseTasks, ...adjustmentTasks];
    
    for (const task of allTasks) {
      // Create unique key from referenceNumber, description, and salesOfficeId (excludes notes)
      const key = `${task.referenceNumber}|${task.description}|${task.salesOfficeId}`;
      
      if (taskMap.has(key)) {
        // Aggregate hours if task already exists
        const existing = taskMap.get(key)!;
        existing.pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor);
        existing.eng += this.replaceNegativeInfiniteNine(task.engineerLabor);
        existing.ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
        existing.eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
        existing.leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
        existing.neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
        existing.qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);
        existing.grap += this.replaceNegativeInfiniteNine(task.graphicsLabor);
        existing.prog += this.replaceNegativeInfiniteNine(task.programmingLabor);
        existing.spec += this.replaceNegativeInfiniteNine(task.specialistLabor);
        existing.dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
        existing.mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
        existing.elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
      } else {
        // Create new entry (notes not included in grouping)
        taskMap.set(key, {
          referenceNumber: task.referenceNumber,
          description: task.description,
          notes: "",
          salesOfficeId: task.salesOfficeId,
          pm: this.replaceNegativeInfiniteNine(task.projectManagementLabor),
          eng: this.replaceNegativeInfiniteNine(task.engineerLabor),
          ceng: this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor),
          eeng: this.replaceNegativeInfiniteNine(task.energyEngineerLabor),
          leng: this.replaceNegativeInfiniteNine(task.lengEngineerLabor),
          neng: this.replaceNegativeInfiniteNine(task.networkEngineerLabor),
          qeng: this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor),
          grap: this.replaceNegativeInfiniteNine(task.graphicsLabor),
          prog: this.replaceNegativeInfiniteNine(task.programmingLabor),
          spec: this.replaceNegativeInfiniteNine(task.specialistLabor),
          dds: this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor),
          mech: this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor),
          elec: this.replaceNegativeInfiniteNine(task.electricalEngineerLabor)
        });
      }
    }
    
    return taskMap;
  }

  // Reusable method to process tasks for Task by Level report (without notes column)
  private processTasksByLevelWithoutNotes(
    levelLaborData: LaborData, 
    levelName: string, 
    multiplier: number = CONSTANTS.ONE,
    includeDxrBaseTasks: boolean = false): void {
    // Group and aggregate tasks (3-field key: no notes)
    const validTasks = this.aggregateLevelTasks(levelLaborData, multiplier, includeDxrBaseTasks,
      (base, adjustment) => this.groupAndAggregateTasksWithoutNotes(base, adjustment));
    
    // Only create table if there are valid tasks
    if (validTasks.length === 0) {
      return;
    }
    
    // Create table
    this.generateTaskByLevelTable(levelName);
    const tableIndex = this.taskByLevelInfoList.length - CONSTANTS.ONE;
    
    // Footer accumulator
    const footerSum = {
      pm: CONSTANTS.ZERO, eng: CONSTANTS.ZERO, ceng: CONSTANTS.ZERO, eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO, neng: CONSTANTS.ZERO, qeng: CONSTANTS.ZERO, grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO, spec: CONSTANTS.ZERO, dds: CONSTANTS.ZERO, mech: CONSTANTS.ZERO, elec: CONSTANTS.ZERO
    };
    
    // Add rows and accumulate into summary
    for (const task of validTasks) {
      this.taskByLevelInfoList[tableIndex].data.push({
        data: [
          { data: task.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: task.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: this.formatHours(task.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.getSalesOfficeDescriptionById(task.salesOfficeId), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });
      
      // Accumulate footer totals
      footerSum.pm += task.pm;
      footerSum.eng += task.eng;
      footerSum.ceng += task.ceng;
      footerSum.eeng += task.eeng;
      footerSum.leng += task.leng;
      footerSum.neng += task.neng;
      footerSum.qeng += task.qeng;
      footerSum.grap += task.grap;
      footerSum.prog += task.prog;
      footerSum.spec += task.spec;
      footerSum.dds += task.dds;
      footerSum.mech += task.mech;
      footerSum.elec += task.elec;
      
      // Accumulate into summary map (reuse existing method)
      this.accumulateTaskIntoSummary(task);
    }
    
    // Add footer only if there are non-zero totals
    if (this.hasNonZeroLaborHours(footerSum)) {
      this.taskByLevelInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.translate.instant("HOURS_TOTAL"), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.formatHours(footerSum.pm), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.eng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.ceng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.eeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.leng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.neng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.qeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.grap), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.prog), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.spec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.dds), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.mech), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(footerSum.elec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "center" }
        ]
      });
    }
  }

  private generateTaskByLevelSummaryTable(): void {
    // Filter out zero-hour tasks from summary
    const summaryTasks = Array.from(this.taskByLevelSummaryMap.values()).filter(task => this.hasNonZeroLaborHours(task));
    
    // Only create summary table if there are valid tasks
    if (summaryTasks.length === 0) {
      return;
    }
    
    // Create summary table
    this.generateTaskByLevelTable('SUMMARY');
    const tableIndex = this.taskByLevelInfoList.length - CONSTANTS.ONE;
    
    // Footer accumulator
    const summaryFooterSum = {
      pm: CONSTANTS.ZERO, eng: CONSTANTS.ZERO, ceng: CONSTANTS.ZERO, eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO, neng: CONSTANTS.ZERO, qeng: CONSTANTS.ZERO, grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO, spec: CONSTANTS.ZERO, dds: CONSTANTS.ZERO, mech: CONSTANTS.ZERO, elec: CONSTANTS.ZERO
    };
    
    // Add rows from summary
    for (const task of summaryTasks) {
      this.taskByLevelInfoList[tableIndex].data.push({
        data: [
          { data: task.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: task.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: this.formatHours(task.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(task.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.getSalesOfficeDescriptionById(task.salesOfficeId), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });
      
      // Accumulate footer totals
      this.accumulateFooterTotals(summaryFooterSum, task);
    }
    
    // Add footer only if there are non-zero totals
    if (this.hasNonZeroLaborHours(summaryFooterSum)) {
      this.taskByLevelInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.translate.instant("HOURS_TOTAL"), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: this.formatHours(summaryFooterSum.pm), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.eng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.ceng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.eeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.leng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.neng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.qeng), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.grap), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.prog), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.spec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.dds), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.mech), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: this.formatHours(summaryFooterSum.elec), columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: "", columnspan: CONSTANTS.ONE, alignment: "center" }
        ]
      });
    }
    
    // Move summary table to the beginning
    if (this.taskByLevelInfoList.length > CONSTANTS.ONE) {
      const summaryTable = this.taskByLevelInfoList.pop();
      if (summaryTable) {
        this.taskByLevelInfoList.unshift(summaryTable);
      }
    }
  }

  private generateTaskByLevelTable(levelName: string): void {
    this.taskByLevelInfoList.push({
      tableHeader: this.reportsCommonService.generateReportTableHeader(levelName),
      title: this.generateTaskByLevelTitleHeaderRow(),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false,
    });
  }

  private generateTaskByLevelTitleHeaderRow(): IDataItem[] {
    return [
      { data: this.translate.instant('TASK_ID'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "35" },
      { data: this.translate.instant('TASK_DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
      { data: this.translate.instant('PM'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('ENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('CENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('EENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('LENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('NENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('QENG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('GRAP'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('PROG'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('SPEC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('DDS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('MECH'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('ELEC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('SERVICING_OFFICE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "80" }
    ];
  }

  // Process project-level tasks for Task by Level report
  private getProjectTaskByLevelWithoutNotes(): void {
    const projectLaborData = this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId];
    this.processTasksByLevelWithoutNotes(projectLaborData, 'ESTIMATE_LEVEL');
  }

  // Process area-level tasks for Task by Level report
  private getAreaTaskByLevelWithoutNotes(area: SQWebArea): void {
    const areaLaborData = this.laborLevelwiseData.laborsCollection[area.itemUniqueId];
    this.processTasksByLevelWithoutNotes(areaLaborData, area.itemName);
  }

  // Process system-level tasks for Task by Level report
  private getSystemTaskByLevelWithoutNotes(
    system: SQWebSystem, 
    laborDataId: string = CONSTANTS.EMPTY_STRING, 
    typicalOfCount: number = CONSTANTS.ZERO,
    visibleTypicalOfCount: number = typicalOfCount): void {
    
    const dataId = laborDataId || system.itemUniqueId;
    const systemLaborData = this.laborLevelwiseData.laborsCollection[dataId];
    const levelName = typicalOfCount ? `${CONSTANTS.TYPICAL_OF_LOGO} of ${typicalOfCount} ${system.itemName}` : system.itemName;
    const multiplier = visibleTypicalOfCount ? visibleTypicalOfCount : CONSTANTS.ONE;
    this.processTasksByLevelWithoutNotes(systemLaborData, levelName, multiplier, this.isDxrMasterSystem(system));
  }

  // Main entry point - processes all hierarchy levels
  private processAllLevels(): void {    
    this.processedTypicalSystemIdsForTaskReports.clear();

    this.getProjectLevelHours();
    this.getProjectLevelHoursByLevel();
    this.getProjectTaskByLevel();
    this.getProjectTaskByLevelWithoutNotes();
    
    //process all areas and their children recursively for all reports
    for (const area of this.sqWebProject.areas) {
      // Hidden areas (and everything under them) must not contribute to any report.
      if (!area.isItemVisible) {
        continue;
      }
      this.processAreaLevel(area);
    }
    
    // Generate summary tables for both Task By Level reports
    this.generateTaskByLevelWithNotesSummaryTable();
    this.generateTaskByLevelSummaryTable();
    // Generate Estimate Total Hours summary table for Labor Hours by Level report
    this.generateEstimateTotalHoursTable();
    // Generate Labor Hours by Office summary table
    this.generateLaborHoursByOffice();
  }

  private getProjectLevelHoursByLevel(): void {
    let projectBaseHours: BaseTasks = this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId].baseTasksCollection;
    let levelByHoursProjectSum : LaborHoursByLevelSum = ["","",CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO];

    this.generateLaborHoursByLevelTable('PROJECT_LEVEL');
    
    // Add all rows and accumulate totals during iteration
    this.calculateProjectBaseHours(projectBaseHours, levelByHoursProjectSum);
    this.calculateTasksHours(levelByHoursProjectSum);
    this.calculateConditionHours(levelByHoursProjectSum);
    
    // Add footer row with accumulated totals
    this.addLaborByHoursFooterRow(levelByHoursProjectSum);
  }

  private calculateProjectBaseHours(projectBaseHours: BaseTasks, levelByHoursProjectSum: LaborHoursByLevelSum): void {
    let summedHours = {
      pm: CONSTANTS.ZERO,
      eng: CONSTANTS.ZERO,
      ceng: CONSTANTS.ZERO,
      eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO,
      neng: CONSTANTS.ZERO,
      qeng: CONSTANTS.ZERO,
      grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO,
      spec: CONSTANTS.ZERO,
      dds: CONSTANTS.ZERO,
      mech: CONSTANTS.ZERO,
      elec: CONSTANTS.ZERO
    };
    
    
    projectBaseHours.forEach(baseTask => {
      summedHours.pm += this.replaceNegativeInfiniteNine(baseTask.projectManagementLabor);
      summedHours.eng += this.replaceNegativeInfiniteNine(baseTask.engineerLabor);
      summedHours.ceng += this.replaceNegativeInfiniteNine(baseTask.cyberSecEngineerLabor);
      summedHours.eeng += this.replaceNegativeInfiniteNine(baseTask.energyEngineerLabor);
      summedHours.leng += this.replaceNegativeInfiniteNine(baseTask.lengEngineerLabor);
      summedHours.neng += this.replaceNegativeInfiniteNine(baseTask.networkEngineerLabor);
      summedHours.qeng += this.replaceNegativeInfiniteNine(baseTask.qualificationEngineerLabor);
      summedHours.grap += this.replaceNegativeInfiniteNine(baseTask.graphicsLabor);
      summedHours.prog += this.replaceNegativeInfiniteNine(baseTask.programmingLabor);
      summedHours.spec += this.replaceNegativeInfiniteNine(baseTask.specialistLabor);
      summedHours.dds += this.replaceNegativeInfiniteNine(baseTask.digitalDeploymentLabor);
      summedHours.mech += this.replaceNegativeInfiniteNine(baseTask.mechanicalEngineerLabor);
      summedHours.elec += this.replaceNegativeInfiniteNine(baseTask.electricalEngineerLabor);
    });
    
    // Add "Project base hours" row as first row
    this.laborHoursByLevelInfoList[CONSTANTS.ZERO].data.push({
      data: [
        { data: this.translate.instant("PROJECT_BASE_HOURS"), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
        { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(summedHours.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
      ]
    });

    // Accumulate to footer sum using helper method (eliminates 13 lines of duplication)
    this.accumulateHoursToSum(levelByHoursProjectSum, summedHours);
    // Accumulate to estimate total base hours
    this.addToFooterSum(this.estimateTotalBaseHours, summedHours);
  }

  private calculateTasksHours(levelByHoursProjectSum: LaborHoursByLevelSum): void {
    let projectTasksCollection = this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId].tasksCollection;

    projectTasksCollection.forEach(task => {
      const pmHours = this.replaceNegativeInfiniteNine(task.projectManagementLabor);
      const engHours = this.replaceNegativeInfiniteNine(task.engineerLabor);
      const graphicsHours = this.replaceNegativeInfiniteNine(task.graphicsLabor);
      const programmingHours = this.replaceNegativeInfiniteNine(task.programmingLabor);
      const specialistHours = this.replaceNegativeInfiniteNine(task.specialistLabor);
      const ddsHours = this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
      const mechHours = this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
      const elecHours = this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
      const cengHours = this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
      const eengHours = this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
      const lengHours = this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
      const nengHours = this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
      const qengHours = this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);

      // Add each task row
      this.laborHoursByLevelInfoList[CONSTANTS.ZERO].data.push({
        data: [
          { data: `${task.referenceNumber} ${task.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      // Accumulate to footer sum using helper method (eliminates 13 lines of duplication)
      const taskProjectHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      this.accumulateHoursToSum(levelByHoursProjectSum, taskProjectHours);
      // Accumulate to estimate total task adjustments
      this.addToFooterSum(this.estimateTotalTaskAdjustments, taskProjectHours);
    });
  }

  private calculateConditionHours(levelByHoursProjectSum: LaborHoursByLevelSum): void {
    let projectConditionsCollection = this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId].conditionsCollection;
    projectConditionsCollection.forEach(condition => {
      const pmHours = this.replaceNegativeInfiniteNine(condition.projectManagementLabor);
      const engHours = this.replaceNegativeInfiniteNine(condition.engineerLabor);
      const graphicsHours = this.replaceNegativeInfiniteNine(condition.graphicsLabor);
      const programmingHours = this.replaceNegativeInfiniteNine(condition.programmingLabor);
      const specialistHours = this.replaceNegativeInfiniteNine(condition.specialistLabor);
      const ddsHours = this.replaceNegativeInfiniteNine(condition.digitalDeploymentLabor);
      const mechHours = this.replaceNegativeInfiniteNine(condition.mechanicalEngineerLabor);
      const elecHours = this.replaceNegativeInfiniteNine(condition.electricalEngineerLabor);
      const cengHours = this.replaceNegativeInfiniteNine(condition.cyberSecEngineerLabor);
      const eengHours = this.replaceNegativeInfiniteNine(condition.energyEngineerLabor);
      const lengHours = this.replaceNegativeInfiniteNine(condition.lengEngineerLabor);
      const nengHours = this.replaceNegativeInfiniteNine(condition.networkEngineerLabor);
      const qengHours = this.replaceNegativeInfiniteNine(condition.qualificationEngineerLabor);

      // Add each condition row
      this.laborHoursByLevelInfoList[CONSTANTS.ZERO].data.push({
        data: [
          { data: `${condition.referenceNumber} ${condition.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      // Accumulate to footer sum using helper method (eliminates 13 lines of duplication)
      const conditionProjectHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      this.accumulateHoursToSum(levelByHoursProjectSum, conditionProjectHours);
      // Accumulate to estimate total conditional adjustments
      this.addToFooterSum(this.estimateTotalConditionalAdjustments, conditionProjectHours);
    });
  }

  private addLaborByHoursFooterRow(levelByHoursProjectSum: LaborHoursByLevelSum): void {
    this.laborHoursByLevelInfoList[CONSTANTS.ZERO].tableFooter.push({
      headers: [
        { headerText: this.translate.instant("TOTAL_ADJUSTED_HOURS"), columnspan: CONSTANTS.ONE, alignment: "left" },
        { headerText: "", columnspan: CONSTANTS.ONE},
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.PM]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.ENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.CENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.EENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.LENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.NENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.QENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.GRAP]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.PROG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.SPEC]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.DDS]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.MECH]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(levelByHoursProjectSum[LaborHoursDistColumnEnum.ELEC]), columnspan: CONSTANTS.ONE, alignment: "center" }
      ]
    });
  }

  private generateLaborHoursByLevelTable(levelName: string): void {
    this.laborHoursByLevelInfoList.push({
      title: this.generateLaborHoursByLevelTitleHeaderRow(levelName),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    });
  }

  private generateLaborHoursByLevelTitleHeaderRow(levelName: string): IDataItem[] {
    return [
      { data: this.translate.instant(levelName), DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR ,  isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "200" },
      { data: this.translate.instant('TYPICAL_OF'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "50" },
      { data: "PM", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "ENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "CENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "EENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "LENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "NENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "QENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "GRAP", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "PROG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "SPEC", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "DDS", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "MECH", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
      { data: "ELEC", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO },
    ];
  }

  private generateTaskByLevelWithNotesTable(levelName: string): void {
    this.taskByLevelWithNotesInfoList.push({
      tableHeader: this.reportsCommonService.generateReportTableHeader(levelName),
      title: this.generateTaskByLevelWithNotesTitleHeaderRow(),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false,
    });
  }
  

  private generateTaskByLevelWithNotesTitleHeaderRow(): IDataItem[] {
    return [
      { data: this.translate.instant('TASK_ID'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "35" },
      { data: this.translate.instant('TASK_DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "60", alignment: "left" },
      { data: "PM", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "ENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "CENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "EENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "LENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "NENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "QENG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "GRAP", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "PROG", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "SPEC", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "DDS", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "MECH", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: "ELEC", columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: "38" },
      { data: this.translate.instant('SERVICING_OFFICE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "80" },
      { data: this.translate.instant('NOTES'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
    ];
  }

  private addAreaTasksToByLevelReport(area: SQWebArea, tableIndex: number, sumArray: LaborHoursByLevelSum): void {
    const areaTasksCollection = this.laborLevelwiseData.laborsCollection[area.itemUniqueId].tasksCollection;

    areaTasksCollection.forEach(task => {
      const pmHours = this.replaceNegativeInfiniteNine(task.projectManagementLabor);
      const engHours = this.replaceNegativeInfiniteNine(task.engineerLabor);
      const graphicsHours = this.replaceNegativeInfiniteNine(task.graphicsLabor);
      const programmingHours = this.replaceNegativeInfiniteNine(task.programmingLabor);
      const specialistHours = this.replaceNegativeInfiniteNine(task.specialistLabor);
      const ddsHours = this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
      const mechHours = this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
      const elecHours = this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
      const cengHours = this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
      const eengHours = this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
      const lengHours = this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
      const nengHours = this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
      const qengHours = this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);

      this.laborHoursByLevelInfoList[tableIndex].data.push({
        data: [
          { data: `${task.referenceNumber} ${task.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      // Accumulate to footer sum using helper method (eliminates 13 lines of duplication)
      const areaTaskHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      this.accumulateHoursToSum(sumArray, areaTaskHours);
      // Accumulate to estimate total task adjustments
      this.addToFooterSum(this.estimateTotalTaskAdjustments, areaTaskHours);
    });
  }

  private addAreaConditionsToByLevelReport(area: SQWebArea, tableIndex: number, sumArray: LaborHoursByLevelSum): void {
    const areaConditionsCollection = this.laborLevelwiseData.laborsCollection[area.itemUniqueId].conditionsCollection;

    areaConditionsCollection.forEach(condition => {
      const pmHours = this.replaceNegativeInfiniteNine(condition.projectManagementLabor);
      const engHours = this.replaceNegativeInfiniteNine(condition.engineerLabor);
      const graphicsHours = this.replaceNegativeInfiniteNine(condition.graphicsLabor);
      const programmingHours = this.replaceNegativeInfiniteNine(condition.programmingLabor);
      const specialistHours = this.replaceNegativeInfiniteNine(condition.specialistLabor);
      const ddsHours = this.replaceNegativeInfiniteNine(condition.digitalDeploymentLabor);
      const mechHours = this.replaceNegativeInfiniteNine(condition.mechanicalEngineerLabor);
      const elecHours = this.replaceNegativeInfiniteNine(condition.electricalEngineerLabor);
      const cengHours = this.replaceNegativeInfiniteNine(condition.cyberSecEngineerLabor);
      const eengHours = this.replaceNegativeInfiniteNine(condition.energyEngineerLabor);
      const lengHours = this.replaceNegativeInfiniteNine(condition.lengEngineerLabor);
      const nengHours = this.replaceNegativeInfiniteNine(condition.networkEngineerLabor);
      const qengHours = this.replaceNegativeInfiniteNine(condition.qualificationEngineerLabor);

      this.laborHoursByLevelInfoList[tableIndex].data.push({
        data: [
          { data: `${condition.referenceNumber} ${condition.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      // Accumulate to footer sum using helper method (eliminates 13 lines of duplication)
      const areaConditionHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      this.accumulateHoursToSum(sumArray, areaConditionHours);
      // Accumulate to estimate total conditional adjustments
      this.addToFooterSum(this.estimateTotalConditionalAdjustments, areaConditionHours);
    });
  }

  private sumTaskHours(tasks: BaseTasks | undefined): LaborHoursFooterSum {
    const total = createEmptyLaborHoursFooterSum();
    for (const task of tasks ?? []) {
      total.pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor);
      total.eng += this.replaceNegativeInfiniteNine(task.engineerLabor);
      total.ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor);
      total.eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor);
      total.leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor);
      total.neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor);
      total.qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor);
      total.grap += this.replaceNegativeInfiniteNine(task.graphicsLabor);
      total.prog += this.replaceNegativeInfiniteNine(task.programmingLabor);
      total.spec += this.replaceNegativeInfiniteNine(task.specialistLabor);
      total.dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor);
      total.mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor);
      total.elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor);
    }
    return total;
  }

  private addSystemRowToByLevelReport(system: SQWebSystem, tableIndex: number, sumArray: LaborHoursByLevelSum, typicalOfCount: number = 0, typicalOfCountInArea: number = 0, laborDataId: string = ''): void {
    // Use master's labor data for typical systems
    const dataId = laborDataId || system.itemUniqueId;
    const systemLaborData = this.laborLevelwiseData.laborsCollection[dataId];

    // typicalOfCountInArea is already visible-only, so it's used for both display and hours
    const multiplier = typicalOfCountInArea ? typicalOfCountInArea : CONSTANTS.ONE;

    // Base hours scale with the typical-of count; DXR base hours are one-time (see isDxrMasterSystem),
    // added unscaled and only the first time a typical-of group appears so a group spanning areas isn't double counted.
    const systemTotalHours = createEmptyLaborHoursFooterSum();
    this.addToFooterSum(systemTotalHours, this.sumTaskHours(systemLaborData.baseTasksCollection), multiplier);
    const typicalId = system.systemTypicalOfHelperUniqueId;
    if (this.isDxrMasterSystem(system) && !(typicalId && this.dxrCountedTypicalIds.has(typicalId))) {
      if (typicalId) {
        this.dxrCountedTypicalIds.add(typicalId);
      }
      this.addToFooterSum(systemTotalHours, this.sumTaskHours(systemLaborData.baseTasksDXRCollection));
    }

    const translatedInArea = this.translate.instant("IN_AREA");
    const typicalOfDisplay = typicalOfCount ? `${typicalOfCount} [ ${typicalOfCountInArea} ${translatedInArea} ]` : "";

    // Add single system row with multiplied totals
    this.laborHoursByLevelInfoList[tableIndex].data.push({
      data: [
        { data: this.translate.instant("SYSTEM") + ": " + system.itemName, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", textColor: CONSTANTS.REPORT_DARK_BLUE_COLOR },
        { data: typicalOfDisplay, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.formatHours(systemTotalHours.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
      ]
    });

    // Accumulate system totals (already multiplied) to area footer
    this.accumulateHoursToSum(sumArray, systemTotalHours);
    // Accumulate to estimate total base hours
    this.addToFooterSum(this.estimateTotalBaseHours, systemTotalHours);
  }

  private addSystemTasksToByLevelReport(
    system: SQWebSystem, 
    tableIndex: number, 
    laborDataId: string,
    multiplier: number = CONSTANTS.ONE,
    sumArray?: LaborHoursByLevelSum): void {
    const dataId = laborDataId || system.itemUniqueId;
    const systemTasksCollection = this.laborLevelwiseData.laborsCollection[dataId].tasksCollection;

    systemTasksCollection.forEach(task => {
      const pmHours = this.replaceNegativeInfiniteNine(task.projectManagementLabor) * multiplier;
      const engHours = this.replaceNegativeInfiniteNine(task.engineerLabor) * multiplier;
      const cengHours = this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor) * multiplier;
      const eengHours = this.replaceNegativeInfiniteNine(task.energyEngineerLabor) * multiplier;
      const lengHours = this.replaceNegativeInfiniteNine(task.lengEngineerLabor) * multiplier;
      const nengHours = this.replaceNegativeInfiniteNine(task.networkEngineerLabor) * multiplier;
      const qengHours = this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor) * multiplier;
      const graphicsHours = this.replaceNegativeInfiniteNine(task.graphicsLabor) * multiplier;
      const programmingHours = this.replaceNegativeInfiniteNine(task.programmingLabor) * multiplier;
      const specialistHours = this.replaceNegativeInfiniteNine(task.specialistLabor) * multiplier;
      const ddsHours = this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor) * multiplier;
      const mechHours = this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor) * multiplier;
      const elecHours = this.replaceNegativeInfiniteNine(task.electricalEngineerLabor) * multiplier;

      this.laborHoursByLevelInfoList[tableIndex].data.push({
        data: [
          { data: `  ${task.referenceNumber} ${task.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, alignment: "left" },  // Indented with spaces
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      const systemTaskHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      if (sumArray) {
        this.accumulateHoursToSum(sumArray, systemTaskHours);
      }
      // Accumulate to estimate total task adjustments
      this.addToFooterSum(this.estimateTotalTaskAdjustments, systemTaskHours);
    });
  }

  private addSystemConditionsToByLevelReport(
    system: SQWebSystem, 
    tableIndex: number, 
    laborDataId: string,
    multiplier: number = CONSTANTS.ONE,
    sumArray?: LaborHoursByLevelSum): void {

    const dataId = laborDataId || system.itemUniqueId;
    const systemConditionsCollection = this.laborLevelwiseData.laborsCollection[dataId].conditionsCollection;

    systemConditionsCollection.forEach(condition => {
      const pmHours = this.replaceNegativeInfiniteNine(condition.projectManagementLabor)* multiplier;
      const engHours = this.replaceNegativeInfiniteNine(condition.engineerLabor)* multiplier;
      const graphicsHours = this.replaceNegativeInfiniteNine(condition.graphicsLabor)* multiplier;
      const programmingHours = this.replaceNegativeInfiniteNine(condition.programmingLabor)* multiplier;
      const specialistHours = this.replaceNegativeInfiniteNine(condition.specialistLabor)* multiplier;
      const ddsHours = this.replaceNegativeInfiniteNine(condition.digitalDeploymentLabor)* multiplier;
      const mechHours = this.replaceNegativeInfiniteNine(condition.mechanicalEngineerLabor)* multiplier;
      const elecHours = this.replaceNegativeInfiniteNine(condition.electricalEngineerLabor)* multiplier;
      const cengHours = this.replaceNegativeInfiniteNine(condition.cyberSecEngineerLabor)* multiplier;
      const eengHours = this.replaceNegativeInfiniteNine(condition.energyEngineerLabor)* multiplier;
      const lengHours = this.replaceNegativeInfiniteNine(condition.lengEngineerLabor)* multiplier;
      const nengHours = this.replaceNegativeInfiniteNine(condition.networkEngineerLabor)* multiplier;
      const qengHours = this.replaceNegativeInfiniteNine(condition.qualificationEngineerLabor)* multiplier;

      this.laborHoursByLevelInfoList[tableIndex].data.push({
        data: [
          { data: `  ${condition.referenceNumber} ${condition.description}`, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, alignment: "left" },  // Indented with spaces
          { data: "", isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(pmHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(engHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(cengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(eengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(lengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(nengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(qengHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(graphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(programmingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(specialistHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(ddsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(mechHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
          { data: this.formatHours(elecHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
        ]
      });

      const systemConditionHours: LaborHoursFooterSum = {
        pm: pmHours, eng: engHours, ceng: cengHours, eeng: eengHours,
        leng: lengHours, neng: nengHours, qeng: qengHours, grap: graphicsHours,
        prog: programmingHours, spec: specialistHours, dds: ddsHours, mech: mechHours, elec: elecHours
      };
      if (sumArray) {
        this.accumulateHoursToSum(sumArray, systemConditionHours);
      }
      // Accumulate to estimate total conditional adjustments
      this.addToFooterSum(this.estimateTotalConditionalAdjustments, systemConditionHours);
    });
  }

  private addLaborByHoursFooterRowForIndex(tableIndex: number, sumArray: LaborHoursByLevelSum): void {
    this.laborHoursByLevelInfoList[tableIndex].tableFooter.push({
      headers: [
        { headerText: this.translate.instant("TOTAL_ADJUSTED_HOURS"), columnspan: CONSTANTS.ONE, alignment: "left" },
        { headerText: "", columnspan: CONSTANTS.ONE},
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.PM]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.ENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.CENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.EENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.LENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.NENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.QENG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.GRAP]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.PROG]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.SPEC]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.DDS]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.MECH]), columnspan: CONSTANTS.ONE, alignment: "center" },
        { headerText: this.formatHours(sumArray[LaborHoursDistColumnEnum.ELEC]), columnspan: CONSTANTS.ONE, alignment: "center" }
      ]
    });
  }

  private shouldSkipSystem(systemItem: SQWebSystem, areaId: string): boolean {
    const typicalId = systemItem.systemTypicalOfHelperUniqueId;
    if (!typicalId) {
      return false;
    }
    if (!this.processedTypicalSystemIdsPerArea.has(areaId)) {
      this.processedTypicalSystemIdsPerArea.set(areaId, new Set());
    }
    const areaProcessedSet = this.processedTypicalSystemIdsPerArea.get(areaId)!;
    if (areaProcessedSet.has(typicalId)) {
      return true;
    }
    areaProcessedSet.add(typicalId);
    return false;
  }

  private processAreaLevel(area: SQWebArea): void {
    // CONSTANTS.ONE. Create tables for both reports
    this.generateLaborHoursDistributionTableByName(area.itemName, area.hasTypicalOf);
    this.generateLaborHoursByLevelTable(area.itemName);

    const areaTableIndexDist = this.laborHoursDistRowInfoList.length - CONSTANTS.ONE;
    const areaTableIndexByLevel = this.laborHoursByLevelInfoList.length - CONSTANTS.ONE;
    
    this.getAreaLevelHours(area, areaTableIndexDist);
    
    let levelByHoursAreaSum: LaborHoursByLevelSum = ["","",CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO,CONSTANTS.ZERO];
    
    // Add area's own tasks and conditions to ByLevel report
    this.addAreaTasksToByLevelReport(area, areaTableIndexByLevel, levelByHoursAreaSum);
    this.addAreaConditionsToByLevelReport(area, areaTableIndexByLevel, levelByHoursAreaSum);
    this.getAreaTaskByLevel(area);
    this.getAreaTaskByLevelWithoutNotes(area);

    const areaId = area.itemUniqueId;

    // Single iteration over children
    for (let item of area.children) {
      const anyItem = item as any;
      // Hidden systems/areas are skipped before typical-of de-duplication, so the next visible member
      // of a typical-of group becomes the one that is reported.
      if (!anyItem.isItemVisible) {
        continue;
      }

      // Resolve labor data ID — for typical systems, use master's ID
      let laborDataId = anyItem.itemUniqueId;
      if (anyItem.nodeType === 'BSETSystem') {
        const typicalId = (item as SQWebSystem).systemTypicalOfHelperUniqueId;
        if (typicalId) {
          const helper = this.typicalOfHelpers[typicalId];
          if (helper?.typicalOfCollectionHolderId) {
            laborDataId = helper.typicalOfCollectionHolderId;
          }
        }
      }

      // Safety Check: verify labor data exists (using master's ID for typical systems)
      if(this.laborLevelwiseData.laborsCollection[laborDataId]?.stdSalesOfficesOtPwrAdjustmentsCollection){

        if (anyItem.nodeType === 'BSETSystem'){
          const system = item as SQWebSystem;

          // Check if this is a duplicate typical system in this area
          const isTypicalDuplicate = this.shouldSkipSystem(system, areaId);

          // Compute typical-of values once for this system. The overall count (1st column) stays
          // the actual total; the in-area count (2nd column) reflects only visible systems.
          let typicalOfCount = 0;
          let typicalOfCountInArea = 0;
          let visibleTypicalOfCount = 0;
          const typicalId = system.systemTypicalOfHelperUniqueId;
          if (typicalId) {
            const helper = this.typicalOfHelpers[typicalId];
            const allTypicalSystems = helper?.typicalOfSystems ?? [];
            typicalOfCount = allTypicalSystems.length || 1;
            // Exclude hidden systems from the typical-of group for the in-area count and hours
            const visibleTypicalSystems = allTypicalSystems.filter(s => s.isItemVisible);
            visibleTypicalOfCount = visibleTypicalSystems.length || 1;
            typicalOfCountInArea = visibleTypicalSystems.filter(s => s.parentUniqueId === areaId).length || 1;
          }

          if (!isTypicalDuplicate) {
            // Distribution report: Create separate table for this system (first occurrence only)
            this.processSystemLevel(system, typicalOfCount, typicalOfCountInArea, laborDataId);

            // ByLevel report: Add system data to parent area's table (first occurrence only)
            const systemMultiplier = typicalOfCountInArea ? typicalOfCountInArea : CONSTANTS.ONE;
            this.addSystemRowToByLevelReport(system, areaTableIndexByLevel, levelByHoursAreaSum, typicalOfCount, typicalOfCountInArea, laborDataId);
            this.addSystemTasksToByLevelReport(system, areaTableIndexByLevel, laborDataId, systemMultiplier, levelByHoursAreaSum);
            this.addSystemConditionsToByLevelReport(system, areaTableIndexByLevel, laborDataId, systemMultiplier, levelByHoursAreaSum);
          }

          // Task by Level reports: skip global duplicates, show typical-of header, multiply values
          if (!typicalId || !this.processedTypicalSystemIdsForTaskReports.has(typicalId)) {
            if (typicalId) {
              this.processedTypicalSystemIdsForTaskReports.add(typicalId);
            }
            this.getSystemTaskByLevel(system, laborDataId, typicalOfCount, visibleTypicalOfCount);
            this.getSystemTaskByLevelWithoutNotes(system, laborDataId, typicalOfCount, visibleTypicalOfCount);
          }
          
        } else if (anyItem.nodeType === 'Area') {
          this.processAreaLevel(item as SQWebArea);
        }
      }
    }
    
    // Add footer for ByLevel report
    this.addLaborByHoursFooterRowForIndex(areaTableIndexByLevel, levelByHoursAreaSum);
  }

  // Get hours for area level
  private getAreaLevelHours(area: SQWebArea, tableIndex: number): void {
    const otCollection: OtPwrSalesOfficeCollection = this.commonDataService.globalPPMmode 
      ? this.laborLevelwiseData.laborsCollection[area.itemUniqueId].ppmSalesOfficesOtPwrAdjustmentsCollection
      : this.laborLevelwiseData.laborsCollection[area.itemUniqueId].stdSalesOfficesOtPwrAdjustmentsCollection;
    
    const rowSumArray = this.createEmptyRowSumArray();

    // Process OT/PWR data
    for (let otPwrSalesOffices of otCollection) {
      for (const otPWRRow of otPwrSalesOffices.overTimePWRAdjustments) {
        this.fillOtPWRRowBasedOnDescription(otPWRRow, rowSumArray);
      }
    }

    // Calculate footer totals
    const footerSumArr = this.calculateFooterTotals(rowSumArray);
    this.addRowsToDistributionTable(tableIndex, rowSumArray);
    this.addFooterToDistributionTable(tableIndex, footerSumArr);
  }

  // Process system level (creates separate table for each system)
  private processSystemLevel(
    system: SQWebSystem, 
    typicalOfCount: number,
    typicalOfCountInArea: number, 
    laborDataId: string): void {
      
    // Create table for this system (title shows the actual total count; in-area count is visible-only)
    this.generateLaborHoursDistributionTableByName(system.itemName, system.hasTypicalOf, typicalOfCount, typicalOfCountInArea);
    const systemTableIndex = this.laborHoursDistRowInfoList.length - CONSTANTS.ONE;

    // Use master's labor data for typical systems
    const dataId = laborDataId || system.itemUniqueId;
    const otCollection: OtPwrSalesOfficeCollection = this.commonDataService.globalPPMmode 
      ? this.laborLevelwiseData.laborsCollection[dataId].ppmSalesOfficesOtPwrAdjustmentsCollection
      : this.laborLevelwiseData.laborsCollection[dataId].stdSalesOfficesOtPwrAdjustmentsCollection;
    
    const rowSumArray = this.createEmptyRowSumArray();
    
    for (let otPwrSalesOffices of otCollection) {
      for (const otPWRRow of otPwrSalesOffices.overTimePWRAdjustments) {
        this.fillOtPWRRowBasedOnDescription(otPWRRow, rowSumArray);
      }
    }

    // typicalOfCountInArea is already visible-only, so it's used directly as the hours multiplier
    const multiplier = typicalOfCountInArea || CONSTANTS.ONE;
    if (multiplier > CONSTANTS.ONE) {
      for (const row of rowSumArray) {
        for (let col = LaborHoursDistColumnEnum.PM; col <= LaborHoursDistColumnEnum.ELEC; col++) {
          (row[col] as number) *= multiplier;
        }
      }
    }

    // Calculate footer totals (already reflects multiplied values)
    const footerSumArr = this.calculateFooterTotals(rowSumArray);
    this.addRowsToDistributionTable(systemTableIndex, rowSumArray);
    this.addFooterToDistributionTable(systemTableIndex, footerSumArr);
  }

  private getProjectLevelHours(): void {

    //REPORT GENERATED
    this.generateLaborHoursDistributionTableByName('PROJECT_LEVEL');
    
      const otCollection: OtPwrSalesOfficeCollection = this.commonDataService.globalPPMmode 
        ? this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId].ppmSalesOfficesOtPwrAdjustmentsCollection
        : this.laborLevelwiseData.laborsCollection[this.sqWebProject.itemUniqueId].stdSalesOfficesOtPwrAdjustmentsCollection;
      
      const rowSumArray = this.createEmptyRowSumArray();
      
      for (let otPwrSalesOffices of otCollection) {
        for (const otPWRRow of otPwrSalesOffices.overTimePWRAdjustments) {
          this.fillOtPWRRowBasedOnDescription(otPWRRow, rowSumArray);
        }        
      }

      const footerSumArr = this.calculateFooterTotals(rowSumArray);
      this.addRowsToDistributionTable(CONSTANTS.ZERO, rowSumArray);
      this.addFooterToDistributionTable(CONSTANTS.ZERO, footerSumArr);

      

      console.log("data after getProjectLevelHours: ", rowSumArray);
      
  }

  private fillOtPWRRowBasedOnDescription(otPWRRow: OverTimePWRAdjustment, rowSumArray: RowSumArray): void {

      switch (otPWRRow.description) {
        case 'Normal Hours Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.STRAIGHT_TIME, otPWRRow, rowSumArray);
          break;
        case 'Shift Differential Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.SHIFT_DIFFERENTIAL_RATE, otPWRRow, rowSumArray);
          break;
        case 'Overtime @ Time and 1/2 Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.OVERTIME_TIME_AND_1_2, otPWRRow, rowSumArray);
          break;
        case 'Overtime @ Double Time Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.OVERTIME_DOUBLE_TIME, otPWRRow, rowSumArray);
          break;
        case 'Prevailing Wage Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.PREVAILING_WAGE_RATE, otPWRRow, rowSumArray);
          break;
        case 'PWR @ Shift Differential Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.PWR_SHIFTS_DIFFERENTIAL_RATE, otPWRRow, rowSumArray);
          break;
        case 'PWR @ Time and 1/2 Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.PREVAILING_WAGE_RATE_TIME_AND_1_2, otPWRRow, rowSumArray);
          break;
        case 'PWR @ Double Time Rate':
          this.patchRowSumValue(LaborHoursDistRowEnum.PREVAILING_WAGE_RATE_DOUBLE_TIME, otPWRRow, rowSumArray);
          break;
      }

  }

  private patchRowSumValue(rowName: LaborHoursDistRowEnum, otPWRRow: OverTimePWRAdjustment, rowSumArray: RowSumArray) {
    rowSumArray[rowName][LaborHoursDistColumnEnum.PM] += this.replaceNegativeInfiniteNine(otPWRRow.projectManagementLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.ENG] += this.replaceNegativeInfiniteNine(otPWRRow.engineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.CENG] += this.replaceNegativeInfiniteNine(otPWRRow.cyberSecEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.EENG] += this.replaceNegativeInfiniteNine(otPWRRow.energyEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.LENG] += this.replaceNegativeInfiniteNine(otPWRRow.lengEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.NENG] += this.replaceNegativeInfiniteNine(otPWRRow.networkEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.QENG] += this.replaceNegativeInfiniteNine(otPWRRow.qualificationEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.GRAP] += this.replaceNegativeInfiniteNine(otPWRRow.graphicsLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.PROG] += this.replaceNegativeInfiniteNine(otPWRRow.programmingLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.SPEC] += this.replaceNegativeInfiniteNine(otPWRRow.specialistLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.DDS] += this.replaceNegativeInfiniteNine(otPWRRow.digitalDeploymentLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.MECH] += this.replaceNegativeInfiniteNine(otPWRRow.mechanicalEngineerLabor);
    rowSumArray[rowName][LaborHoursDistColumnEnum.ELEC] += this.replaceNegativeInfiniteNine(otPWRRow.electricalEngineerLabor);
  }

  // ========== Labor Hours By Office (new) ==========
  private generateLaborHoursByOfficeTitleHeaderRow(): IDataItem[] {
    return [
      { data: this.translate.instant('LABOR_TYPE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
      { data: this.translate.instant('BASE_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
      { data: this.translate.instant('ADJUSTED_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
      { data: this.translate.instant('COST_TOTAL'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
    ];
  }

  // Add office discipline values into totals
  private accumulateOfficeTotals(
    totals: IOfficeLaborTotals,
    agg: IOfficeLaborTotals
  ): void {
    totals.pm += agg.pm;
    totals.eng += agg.eng;
    totals.ceng += agg.ceng;
    totals.eeng += agg.eeng;
    totals.leng += agg.leng;
    totals.neng += agg.neng;
    totals.qeng += agg.qeng;
    totals.grap += agg.grap;
    totals.prog += agg.prog;
    totals.spec += agg.spec;
    totals.dds += agg.dds;
    totals.mech += agg.mech;
    totals.elec += agg.elec;
  }

  // Create an empty totals object for office aggregations
  private createEmptyOfficeTotals(): IOfficeLaborTotals {
    return {
      pm: CONSTANTS.ZERO,
      eng: CONSTANTS.ZERO,
      ceng: CONSTANTS.ZERO,
      eeng: CONSTANTS.ZERO,
      leng: CONSTANTS.ZERO,
      neng: CONSTANTS.ZERO,
      qeng: CONSTANTS.ZERO,
      grap: CONSTANTS.ZERO,
      prog: CONSTANTS.ZERO,
      spec: CONSTANTS.ZERO,
      dds: CONSTANTS.ZERO,
      mech: CONSTANTS.ZERO,
      elec: CONSTANTS.ZERO
    };
  }

  // Helper: build standardized rows for office tables
  private buildOfficeRows(
    baseAgg: IOfficeLaborTotals,
    adjAgg: IOfficeLaborTotals
  ): Array<{ label: string; base: number; adj: number }> {
    return [
      { label: this.translate.instant('PM_LABOR'), base: baseAgg.pm, adj: adjAgg.pm },
      { label: this.translate.instant('ENG_LABOR'), base: baseAgg.eng, adj: adjAgg.eng },
      { label: this.translate.instant('CYBER_SECURITY_ENGINEER'), base: baseAgg.ceng, adj: adjAgg.ceng },
      { label: this.translate.instant('ENERGY_ENGINEER'), base: baseAgg.eeng, adj: adjAgg.eeng },
      { label: this.translate.instant('ELECTRICAL_ENGINEER'), base: baseAgg.leng, adj: adjAgg.leng },
      { label: this.translate.instant('NETWORK_ENGINEER'), base: baseAgg.neng, adj: adjAgg.neng },
      { label: this.translate.instant('QUALIFICATION_ENGINEER'), base: baseAgg.qeng, adj: adjAgg.qeng },
      { label: this.translate.instant('GRAP_LABOR'), base: baseAgg.grap, adj: adjAgg.grap },
      { label: this.translate.instant('PROG_LABOR'), base: baseAgg.prog, adj: adjAgg.prog },
      { label: this.translate.instant('SPEC_LABOR'), base: baseAgg.spec, adj: adjAgg.spec },
      { label: this.translate.instant('DDS_LABOR'), base: baseAgg.dds, adj: adjAgg.dds },
      { label: this.translate.instant('MECH_LABOR'), base: baseAgg.mech, adj: adjAgg.mech },
      { label: this.translate.instant('ELEC_LABOR'), base: baseAgg.elec, adj: adjAgg.elec }
    ];
  }

  // Populate data into a table and compute footer totals
  private populateOfficeTable(
    tableIndex: number,
    rows: Array<{ label: string; base: number; adj: number }>,
    skipZeroRows: boolean = false
  ): { footerBase: number; footerAdj: number; footerCost: number } {
    let footerBase = CONSTANTS.ZERO;
    let footerAdj = CONSTANTS.ZERO;
    let footerCost = CONSTANTS.ZERO;

    for (const item of rows) {
      const rowCostTotal = item.base + item.adj;
      if (skipZeroRows && item.base === CONSTANTS.ZERO && item.adj === CONSTANTS.ZERO) {
        continue;
      }
      this.labourHoursByOfficeInfoList[tableIndex].data.push({
        data: [
          { data: item.label, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: this.formatHours(item.base), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { data: this.formatHours(item.adj), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { data: this.formatHours(rowCostTotal), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT }
        ]
      });

      footerBase += item.base;
      footerAdj += item.adj;
      footerCost += rowCostTotal;
    }

    return { footerBase, footerAdj, footerCost };
  }

  // Add a single task's labor fields into the appropriate office totals map
  private accumulateTaskToOfficeMap(
    officeMap: Map<number, IOfficeLaborTotals>,
    task: {
      salesOfficeId: number;
      projectManagementLabor: number;
      engineerLabor: number;
      cyberSecEngineerLabor: number;
      energyEngineerLabor: number;
      lengEngineerLabor: number;
      networkEngineerLabor: number;
      qualificationEngineerLabor: number;
      graphicsLabor: number;
      programmingLabor: number;
      specialistLabor: number;
      digitalDeploymentLabor: number;
      mechanicalEngineerLabor: number;
      electricalEngineerLabor: number;
    }
    , typicalOfCount: number
  ): void {
    // Skip tasks with invalid office IDs
    if (!task || task.salesOfficeId === null || task.salesOfficeId === undefined) {
      return;
    }
    
    const officeId = task.salesOfficeId;
    const agg = officeMap.get(officeId) || this.createEmptyOfficeTotals();
    agg.pm += this.replaceNegativeInfiniteNine(task.projectManagementLabor) * typicalOfCount;
    agg.eng += this.replaceNegativeInfiniteNine(task.engineerLabor) * typicalOfCount;
    agg.ceng += this.replaceNegativeInfiniteNine(task.cyberSecEngineerLabor) * typicalOfCount;
    agg.eeng += this.replaceNegativeInfiniteNine(task.energyEngineerLabor) * typicalOfCount;
    agg.leng += this.replaceNegativeInfiniteNine(task.lengEngineerLabor) * typicalOfCount;
    agg.neng += this.replaceNegativeInfiniteNine(task.networkEngineerLabor) * typicalOfCount;
    agg.qeng += this.replaceNegativeInfiniteNine(task.qualificationEngineerLabor) * typicalOfCount;
    agg.grap += this.replaceNegativeInfiniteNine(task.graphicsLabor) * typicalOfCount;
    agg.prog += this.replaceNegativeInfiniteNine(task.programmingLabor) * typicalOfCount;
    agg.spec += this.replaceNegativeInfiniteNine(task.specialistLabor) * typicalOfCount;
    agg.dds += this.replaceNegativeInfiniteNine(task.digitalDeploymentLabor) * typicalOfCount;
    agg.mech += this.replaceNegativeInfiniteNine(task.mechanicalEngineerLabor) * typicalOfCount;
    agg.elec += this.replaceNegativeInfiniteNine(task.electricalEngineerLabor) * typicalOfCount;
    officeMap.set(officeId, agg);
  }

  private getOfficeNameFromId(officeId: number): string {
    const cachedName = this.salesOfficeNameById.get(officeId);
    if (cachedName) {
      return cachedName;
    }

    const officeDescription = this.getSalesOfficeDescriptionById(officeId);
    if (officeDescription !== officeId.toString()) {
      this.salesOfficeNameById.set(officeId, officeDescription);
      return officeDescription;
    }

    return `Office ${officeId}`;
  }

  private getSalesOfficeDescriptionById(id: number): string {
    if(!this.salesOfficesDetails){
      return '';
    }
    const office = this.salesOfficesDetails.find(item => item.salesOfficeId === id);
    return office?.salesOfficeName || '';
  }

  private getOfficeTypicalOfResolution(laborsCollection: Record<string, LaborData>): {
    typicalOfMultiplierByLevelId: Map<string, number>;
    typicalOfDuplicateLevelIds: Set<string>;
  } {
    const typicalOfMultiplierByLevelId = new Map<string, number>();
    const typicalOfDuplicateLevelIds = new Set<string>();

    for (const key in this.typicalOfHelpers) {
      const helper = this.typicalOfHelpers[key];
      const holderId = helper?.typicalOfCollectionHolderId;
      if (!holderId || !laborsCollection[holderId]) {
        continue;
      }

      typicalOfMultiplierByLevelId.set(holderId, (helper.typicalOfSystems ?? []).filter(s => s.isItemVisible).length || CONSTANTS.ONE);

      for (const system of helper.typicalOfSystems ?? []) {
        if (system.itemUniqueId && system.itemUniqueId !== holderId && laborsCollection[system.itemUniqueId]) {
          typicalOfDuplicateLevelIds.add(system.itemUniqueId);
        }
      }
    }

    return { typicalOfMultiplierByLevelId, typicalOfDuplicateLevelIds };
  }

  /**
   * Walks the area/system tree once and returns the labor data ids the by-office report needs, because
   * that report iterates the flat labor collection and has no tree context of its own:
   * - hiddenIds: hidden areas/systems and everything under a hidden area. A typical-of group is hidden
   *   only when none of its members is visible (its labor data sits on the holder id).
   * - dxrMasterIds: levels whose DXR base tasks count (see isDxrMasterSystem).
   */
  private getOfficeLevelFlags(): { hiddenIds: Set<string>; dxrMasterIds: Set<string> } {
    const visibleIds = new Set<string>();
    const hiddenIds = new Set<string>();
    const dxrMasterIds = new Set<string>();
    const visit = (children: any[], parentVisible: boolean): void => {
      for (const item of children ?? []) {
        const isVisible = parentVisible && !!item.isItemVisible;
        if (item.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = item as SQWebSystem;
          const typicalId = system.systemTypicalOfHelperUniqueId;
          const levelId = (typicalId && this.typicalOfHelpers[typicalId]?.typicalOfCollectionHolderId) || system.itemUniqueId;
          (isVisible ? visibleIds : hiddenIds).add(levelId);
          if (this.isDxrMasterSystem(system)) {
            dxrMasterIds.add(levelId);
          }
        } else if (item.nodeType === CONSTANTS.NODETYPE_AREA) {
          if (!isVisible) {
            hiddenIds.add(item.itemUniqueId);
          }
          visit((item as SQWebArea).children, isVisible);
        }
      }
    };
    visit(this.sqWebProject?.areas, true);
    visibleIds.forEach(id => hiddenIds.delete(id));
    return { hiddenIds, dxrMasterIds };
  }

  private collectLaborDataByOffice(): { baseOfficeMap: Map<number, IOfficeLaborTotals>; adjustedOfficeMap: Map<number, IOfficeLaborTotals>; } {
    const baseOfficeMap = new Map<number, IOfficeLaborTotals>();
    const adjustedOfficeMap = new Map<number, IOfficeLaborTotals>();

    const laborsCollection = this.laborLevelwiseData.laborsCollection;
    if (!laborsCollection) {
      return { baseOfficeMap, adjustedOfficeMap };
    }

    const { typicalOfMultiplierByLevelId, typicalOfDuplicateLevelIds } = this.getOfficeTypicalOfResolution(laborsCollection);
    const { hiddenIds, dxrMasterIds } = this.getOfficeLevelFlags();

    for (const [levelId, levelLaborData] of Object.entries(laborsCollection)) {
      if (!levelLaborData || typicalOfDuplicateLevelIds.has(levelId) || hiddenIds.has(levelId)) {
        continue;
      }

      const typicalOfCount = typicalOfMultiplierByLevelId.get(levelId) ?? CONSTANTS.ONE;
      this.processBaseTasksForOffice(levelLaborData.baseTasksCollection, baseOfficeMap, typicalOfCount);
      // DXR base tasks are one-time, so they are not scaled by the typical-of count.
      if (dxrMasterIds.has(levelId)) {
        this.processBaseTasksForOffice(levelLaborData.baseTasksDXRCollection, baseOfficeMap, CONSTANTS.ONE);
      }
      this.processAdjustedTasksForOffice(levelLaborData.tasksCollection, adjustedOfficeMap, typicalOfCount);
      this.processConditionsForOffice(levelLaborData.conditionsCollection, adjustedOfficeMap, typicalOfCount);
    }

    return { baseOfficeMap, adjustedOfficeMap };
  }

  private processBaseTasksForOffice(baseTasks: BaseTasks, officeMap: Map<number, IOfficeLaborTotals>, typicalOfCount: number): void {
    if (baseTasks?.length > 0) {
      baseTasks.forEach(task => this.accumulateTaskToOfficeMap(officeMap, task, typicalOfCount));
    }
  }

  private processAdjustedTasksForOffice(adjustedTasks: Tasks, officeMap: Map<number, IOfficeLaborTotals>, typicalOfCount: number): void {
    if (adjustedTasks?.length > 0) {
      adjustedTasks.forEach(task => this.accumulateTaskToOfficeMap(officeMap, task, typicalOfCount));
    }
  }

  private processConditionsForOffice(conditions: Condition[], officeMap: Map<number, IOfficeLaborTotals>, typicalOfCount: number): void {
    if (conditions?.length > 0) {
      conditions.forEach(cond => this.accumulateTaskToOfficeMap(officeMap, cond, typicalOfCount));
    }
  }

  private calculateTotalsFromOfficeMap(officeMap: Map<number, IOfficeLaborTotals>): IOfficeLaborTotals {
    const totals = this.createEmptyOfficeTotals();
    for (const agg of officeMap.values()) {
      this.accumulateOfficeTotals(totals, agg);
    }
    return totals;
  }

  private createOfficeTable(officeName: string, disciplineRows: Array<{ label: string; base: number; adj: number }>): void {
    this.labourHoursByOfficeInfoList.push({
      tableHeader: this.reportsCommonService.generateReportTableHeader(officeName),
      title: this.generateLaborHoursByOfficeTitleHeaderRow(),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    });

    const tableIndex = this.labourHoursByOfficeInfoList.length - CONSTANTS.ONE;
    const { footerBase, footerAdj, footerCost } = this.populateOfficeTable(tableIndex, disciplineRows, true);

    this.labourHoursByOfficeInfoList[tableIndex].tableFooter.push({
      headers: [
        { headerText: this.translate.instant('HOURS_TOTAL'), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { headerText: footerBase.toFixed(CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: footerAdj.toFixed(CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: footerCost.toFixed(CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    });
  }

  private getSortedOfficeIds(baseOfficeMap: Map<number, IOfficeLaborTotals>, adjustedOfficeMap: Map<number, IOfficeLaborTotals>): number[] {
    return Array.from(new Set<number>([
      ...Array.from(baseOfficeMap.keys()),
      ...Array.from(adjustedOfficeMap.keys())
    ])).sort((a, b) => {
      const aName = this.salesOfficeNameById.get(a) ?? '';
      const bName = this.salesOfficeNameById.get(b) ?? '';
      if (!aName && bName) return 1;
      if (aName && !bName) return -1;
      const cmp = aName.localeCompare(bName);
      return cmp === 0 ? a-b : cmp;
    });
  }

  private async generateLaborHoursByOffice(): Promise<void> {
    const { baseOfficeMap, adjustedOfficeMap } = this.collectLaborDataByOffice();

    if (baseOfficeMap.size === 0 && adjustedOfficeMap.size === 0) {
      return;
    }

    // Generate summary table for all offices
    const totalsBase = this.calculateTotalsFromOfficeMap(baseOfficeMap);
    const totalsAdj = this.calculateTotalsFromOfficeMap(adjustedOfficeMap);
    const summaryRows = this.buildOfficeRows(totalsBase, totalsAdj);
    this.createOfficeTable('ALL', summaryRows);

    // Generate individual tables for each office
    const officeIds = this.getSortedOfficeIds(baseOfficeMap, adjustedOfficeMap);
    for (const officeId of officeIds) {
      const baseAgg = baseOfficeMap.get(officeId) || this.createEmptyOfficeTotals();
      const adjAgg = adjustedOfficeMap.get(officeId) || this.createEmptyOfficeTotals();
      const rows = this.buildOfficeRows(baseAgg, adjAgg);
      this.createOfficeTable(this.getOfficeNameFromId(officeId), rows);
    }
  }


  private generateEstimateTotalHoursTable(): void {
    const totalAdjusted: LaborHoursFooterSum = {
      pm: this.estimateTotalBaseHours.pm + this.estimateTotalTaskAdjustments.pm + this.estimateTotalConditionalAdjustments.pm,
      eng: this.estimateTotalBaseHours.eng + this.estimateTotalTaskAdjustments.eng + this.estimateTotalConditionalAdjustments.eng,
      ceng: this.estimateTotalBaseHours.ceng + this.estimateTotalTaskAdjustments.ceng + this.estimateTotalConditionalAdjustments.ceng,
      eeng: this.estimateTotalBaseHours.eeng + this.estimateTotalTaskAdjustments.eeng + this.estimateTotalConditionalAdjustments.eeng,
      leng: this.estimateTotalBaseHours.leng + this.estimateTotalTaskAdjustments.leng + this.estimateTotalConditionalAdjustments.leng,
      neng: this.estimateTotalBaseHours.neng + this.estimateTotalTaskAdjustments.neng + this.estimateTotalConditionalAdjustments.neng,
      qeng: this.estimateTotalBaseHours.qeng + this.estimateTotalTaskAdjustments.qeng + this.estimateTotalConditionalAdjustments.qeng,
      grap: this.estimateTotalBaseHours.grap + this.estimateTotalTaskAdjustments.grap + this.estimateTotalConditionalAdjustments.grap,
      prog: this.estimateTotalBaseHours.prog + this.estimateTotalTaskAdjustments.prog + this.estimateTotalConditionalAdjustments.prog,
      spec: this.estimateTotalBaseHours.spec + this.estimateTotalTaskAdjustments.spec + this.estimateTotalConditionalAdjustments.spec,
      dds: this.estimateTotalBaseHours.dds + this.estimateTotalTaskAdjustments.dds + this.estimateTotalConditionalAdjustments.dds,
      mech: this.estimateTotalBaseHours.mech + this.estimateTotalTaskAdjustments.mech + this.estimateTotalConditionalAdjustments.mech,
      elec: this.estimateTotalBaseHours.elec + this.estimateTotalTaskAdjustments.elec + this.estimateTotalConditionalAdjustments.elec,
    };

    this.laborHoursByLevelInfoList.push({
      title: this.generateLaborHoursByLevelTitleHeaderRow('ESTIMATE_TOTAL_HOURS'),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    });
    const tableIndex = this.laborHoursByLevelInfoList.length - CONSTANTS.ONE;

    const buildDataRow = (labelKey: string, hours: LaborHoursFooterSum) => ({
      data: [
        { data: this.translate.instant(labelKey), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT as 'left' },
        { data: '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.pm), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.eng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.ceng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.eeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.leng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.neng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.qeng), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.grap), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.prog), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.spec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.dds), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.mech), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' },
        { data: this.formatHours(hours.elec), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as 'center' }
      ]
    });

    this.laborHoursByLevelInfoList[tableIndex].data.push(buildDataRow('BASE_HOURS', this.estimateTotalBaseHours));
    this.laborHoursByLevelInfoList[tableIndex].data.push(buildDataRow('TASK_ADJUSTMENTS', this.estimateTotalTaskAdjustments));
    this.laborHoursByLevelInfoList[tableIndex].data.push(buildDataRow('CONDITIONAL_ADJUSTMENTS', this.estimateTotalConditionalAdjustments));

    this.laborHoursByLevelInfoList[tableIndex].tableFooter.push({
      headers: [
        { headerText: this.translate.instant('TOTAL_ADJUSTED_HOURS'), columnspan: CONSTANTS.ONE, alignment: 'left' },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: this.formatHours(totalAdjusted.pm), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.eng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.ceng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.eeng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.leng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.neng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.qeng), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.grap), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.prog), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.spec), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.dds), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.mech), columnspan: CONSTANTS.ONE, alignment: 'center' },
        { headerText: this.formatHours(totalAdjusted.elec), columnspan: CONSTANTS.ONE, alignment: 'center' }
      ]
    });
  }

  private resetValues(): void {
    this.laborHoursDistRowInfoList = [];
    this.laborHoursByLevelInfoList = [];
    this.taskByLevelWithNotesInfoList = [];
    this.taskByLevelInfoList = [];
    this.labourHoursByOfficeInfoList = [];
    Object.assign(this.estimateTotalBaseHours, createEmptyLaborHoursFooterSum());
    Object.assign(this.estimateTotalTaskAdjustments, createEmptyLaborHoursFooterSum());
    Object.assign(this.estimateTotalConditionalAdjustments, createEmptyLaborHoursFooterSum());
    this.salesOfficesDetails = [];
    this.taskByLevelWithNotesSummaryMap = new Map();
    this.taskByLevelSummaryMap = new Map();
    this.processedTypicalSystemIdsPerArea = new Map();
    this.processedTypicalSystemIdsForTaskReports = new Set();
    this.dxrCountedTypicalIds = new Set();
    this.reportGenerated = false;
  }

  ngOnDestroy(): void {
    this.resetValues();
  }

}
